/**
 * The inbox's calls (`MCK-20260521-009` B3).
 *
 * **Not workspace-scoped, and that is the point.** An inbox is keyed on the person, not on the
 * studio they happen to be acting as (`chat1.md` §3, the B3 addendum): somebody who works for
 * two firms owes work in both, and a list that showed one of them would be a list they cannot
 * trust. Every other cross-studio surface in this product — `/tasks`, `/exports` — sits at the
 * root for the same reason and this one joins them.
 */

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/openapi';

type Schemas = components['schemas'];

export type InboxItem = Schemas['InboxItemResponse'];
export type InboxSummary = Schemas['InboxSummaryResponse'];
export type InboxTab = Schemas['InboxTab'];
/**
 * Whose inbox is on screen (owner, 2026-09-16): mine · my studio's · my studio's **minus mine**.
 *
 * Three named values rather than a switch and a flag, because the pair can be asked
 * nonsensically and three values cannot be combined at all. `others` is the one that stops a
 * lead drowning: their own mail is cleared, and what is left is everybody else's.
 */
export type InboxScope = Schemas['InboxScope'];
export type InboxPage = Schemas['Page_InboxItemResponse_'];

/** The four tabs, in the order the screen draws them. TWIN: `INBOX_TABS` in `inbox/models.py`. */
export const INBOX_TABS: readonly InboxTab[] = [
  'assigned',
  'review_awaiting',
  'mention',
  'following',
];

export function listInbox(
  tab: InboxTab | null,
  unreadOnly: boolean,
  scope: InboxScope,
  cursor: string | null,
): Promise<InboxPage> {
  return unwrap(
    api.GET('/api/v1/inbox', {
      params: {
        query: {
          ...(tab === null ? {} : { tab: [tab] }),
          ...(unreadOnly ? { unread: true } : {}),
          // Absent means `me`; sending it always would put a default in every URL for nothing.
          ...(scope === 'me' ? {} : { scope }),
          ...(cursor === null ? {} : { cursor }),
        },
      },
    }),
  );
}

/**
 * The counts, without a page of rows behind them.
 *
 * Its own request because the bell is on every screen and most of them never open the inbox:
 * a count carried on the list response would make the shell fetch rows nothing renders.
 */
export function readInboxSummary(scope: InboxScope = 'me'): Promise<InboxSummary> {
  return unwrap(
    api.GET('/api/v1/inbox/summary', {
      params: { query: scope === 'me' ? {} : { scope } },
    }),
  );
}

/**
 * Mark rows read, unread, dismissed or back again — **by ids or by whole tabs, never both**.
 *
 * `read: false` and `dismissed: false` are undo: somebody who cleared their inbox by accident
 * gets it back instead of asking us to.
 */
export function markInbox(body: {
  readonly item_ids?: readonly string[];
  readonly tabs?: readonly InboxTab[];
  readonly read?: boolean;
  readonly dismissed?: boolean;
}): Promise<Schemas['InboxMarkResponse']> {
  return unwrap(
    api.PATCH('/api/v1/inbox', {
      body: {
        ...(body.item_ids === undefined ? {} : { item_ids: [...body.item_ids] }),
        ...(body.tabs === undefined ? {} : { tabs: [...body.tabs] }),
        ...(body.read === undefined ? {} : { read: body.read }),
        ...(body.dismissed === undefined ? {} : { dismissed: body.dismissed }),
      },
    }),
  );
}
